import {
  FORM_FIELDS,
  methodAtImo2020ComplianceValues,
  NATURE_OF_CALL,
  NATURE_OF_CALL_SPECIFY,
} from "@features/vessel-call/create/constants";
import type { CrewFormSchema } from "@features/vessel-call/create/schema/CrewForm.schema";
import type { PassengerFormValues } from "@features/vessel-call/create/schema/PassengerForm.schema";
import type { VesselCallFormData } from "@features/vessel-call/create/schema/vesselCallFormSchema";
import type { PortCallsSchema } from "@features/vessel-call/create/steps/PreArrivalNotification/PortCalls.schema.ts";
import { sortPortCallsByClosestDates } from "@features/vessel-call/utils/sortPortCallsByClosestDates";
import cloneDeep from "lodash/cloneDeep";
import lodashSet from "lodash/set";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type PortCall = {
  id: string;
  port: string;
  country: string;
  securityLevelOfPortCall: string;
  arrivalDate: string;
  departureDate: string;
  comments: string;
};

export type CrewMember = {
  id: string;
  order?: number;
  familyName: string;
  givenName: string;
  rank: string;
  nationality: string;
  dateOfBirth: string;
  placeOfBirth: string;
  gender: string;
  passportNumber: string;
  issuingState: string;
  expiryDate: string;
};

export type Document = {
  name: string;
  url: string;
  type: string;
};

export type Passenger = {
  id: string;
  order?: number;
  familyName: string;
  givenName: string;
  nationality: string;
  dateOfBirth?: string;
  placeOfBirth: string;
  gender?: string;
  typeOfIdentityDocument?: string;
  identityDocumentNumber: string;
  issuingState: string;
  expiryDate?: string;
  visaNumberIfAppropriate: string;
  portOfEmbarkation: string;
  portOfDisembarkation: string;
  transitPassenger: boolean;
};

export const PERSIST_KEY = "vessel-call-form";

type NatureOfCallKey = (typeof NATURE_OF_CALL)[keyof typeof NATURE_OF_CALL];
type NatureOfCallValue = {
  [K in NatureOfCallKey]: K extends
    | typeof NATURE_OF_CALL.PRIMARY_NATURE_OF_CALL_CODE
    | typeof NATURE_OF_CALL.PRIMARY_NATURE_OF_CALL_DESC
    ? string
    : boolean;
};

const defaultNatureOfCallValues: NatureOfCallValue = Object.values(NATURE_OF_CALL).reduce(
  (acc, val) => {
    const key = val as NatureOfCallKey;
    (acc as Record<NatureOfCallKey, string | boolean>)[key] =
      key === NATURE_OF_CALL.PRIMARY_NATURE_OF_CALL_CODE ||
      key === NATURE_OF_CALL.PRIMARY_NATURE_OF_CALL_DESC
        ? ""
        : false;
    return acc;
  },
  {} as NatureOfCallValue,
);

type SpecifyNatureOfCallKey = (typeof NATURE_OF_CALL_SPECIFY)[keyof typeof NATURE_OF_CALL_SPECIFY];
type SpecifyNatureOfCallValue = Record<SpecifyNatureOfCallKey, string>;

const defaultSpecifyNatureOfCallValues: SpecifyNatureOfCallValue = Object.values(
  NATURE_OF_CALL_SPECIFY,
).reduce((acc, val) => {
  acc[val as SpecifyNatureOfCallKey] = "";
  return acc;
}, {} as SpecifyNatureOfCallValue);

interface VesselCallFormMethods {
  setField<K extends keyof VesselCallFormData>(key: K, val: VesselCallFormData[K]): void;
  setAll(data: Partial<VesselCallFormData>): void;
  resetForm(): void;
  addCrew: (member: CrewMember) => void;
  setCrew: (crew: CrewMember[]) => void;
  addPortCall: (portCall: PortCall) => void;
  setPortCalls: (portCalls: PortCall[]) => void;
  addPassenger: (p: Passenger) => void;
  setPassengers: (ps: Passenger[]) => void;

  setAddPassengerDraft: (draft: Partial<PassengerFormValues> | null) => void;
  clearAddPassengerDraft: () => void;
  setAddCrewDraft: (draft: Partial<CrewFormSchema> | null) => void;
  clearAddCrewDraft: () => void;
  setAddPortCallDraft: (draft: Partial<PortCallsSchema> | null) => void;
  clearAddPortCallDraft: () => void;
}

export interface VesselCallFormState extends VesselCallFormData, VesselCallFormMethods {
  portCalls: PortCall[];
  crew: CrewMember[];
  passengers: Passenger[];
  addPassengerDraft: Partial<PassengerFormValues> | null;
  addCrewDraft: Partial<CrewFormSchema> | null;
  addPortCallDraft: Partial<PortCallsSchema> | null;
}

export const defaultValues: Omit<VesselCallFormState, keyof VesselCallFormMethods> = {
  [FORM_FIELDS.VESSEL_ID]: "",
  [FORM_FIELDS.SELECT_PORT]: "",
  [FORM_FIELDS.EXPECTED_ARRIVAL_LOCATION]: "",
  [FORM_FIELDS.PREFERRED_ANCHORAGE_AREA]: "",
  [FORM_FIELDS.REASON_FOR_ANCHORAGE_PREFERENCE]: "",
  [FORM_FIELDS.MASTERS_NAME]: "",
  [FORM_FIELDS.NATIONALITY]: "",
  [FORM_FIELDS.ETA]: "",
  [FORM_FIELDS.ETD]: "",
  [FORM_FIELDS.OWNERS_MATTERS_AGENT]: "",
  [FORM_FIELDS.LAST_PORT]: "",
  [FORM_FIELDS.NEXT_PORT]: "",
  [FORM_FIELDS.DRAFT_FWD]: "",
  [FORM_FIELDS.DRAFT_AFT]: "",
  [FORM_FIELDS.VESSEL_CONDITION]: "",
  [FORM_FIELDS.CARGO_ONBOARD]: [],
  ...defaultNatureOfCallValues,
  ...defaultSpecifyNatureOfCallValues,
  [FORM_FIELDS.METHOD_AT_IMO_2020_COMPLIANCE]: methodAtImo2020ComplianceValues.compliantFuel,
  [FORM_FIELDS.PORT_FACILITY]: "",
  [FORM_FIELDS.PORT_FACILITY_OTHER]: "",
  [FORM_FIELDS.SECURITY_LEVEL]: "",
  [FORM_FIELDS.ISSC_IS_VALID]: true,
  [FORM_FIELDS.ISSC_NUMBER]: "",
  [FORM_FIELDS.ISSC_ISSUE_DATE]: "",
  [FORM_FIELDS.ISSC_EXPIRY_DATE]: "",
  [FORM_FIELDS.ISSC_ISSUED_BY]: "",
  [FORM_FIELDS.APPROVED_SSP_ONBOARD]: true,
  [FORM_FIELDS.SPECIAL_SECURITY_MEASURES]: false,
  [FORM_FIELDS.TAKEN_MEASURES]: "",
  [FORM_FIELDS.SECURITY_GUARDS_ON_BOARD]: false,
  [FORM_FIELDS.NUMBER_OF_SECURITY_GUARDS]: "",
  [FORM_FIELDS.ARM_AMMUNITION_ON_BOARD]: false,
  [FORM_FIELDS.VESSEL_HAS_DEFICIENCIES]: false,
  [FORM_FIELDS.DEFICIENCIES]: "",
  [FORM_FIELDS.LAST_PORT_CLEARANCE_ISSUE_DATE]: "",
  [FORM_FIELDS.LAST_PORT_CLEARANCE_CLASS_NAME]: "",
  [FORM_FIELDS.FLAG_STATE_PERMISSION_DOCUMENT_URL]: [],
  [FORM_FIELDS.ARM_AMMUNITION_PROOF_DOCUMENT_URL]: [],
  [FORM_FIELDS.DEFICIENCIES_DOCUMENTS_URL]: [],
  [FORM_FIELDS.LAST_PORT_CLEARANCE_DOCUMENT_URL]: [],
  [FORM_FIELDS.NAME_AND_TYPE_OF_SHIP]: "",
  [FORM_FIELDS.IMO_NUMBER]: "",
  [FORM_FIELDS.CALL_SIGN]: "",
  [FORM_FIELDS.VOYAGE_NUMBER]: "",
  [FORM_FIELDS.PORT_OF_ARRIVAL]: "",
  [FORM_FIELDS.DATE_TIME_OF_ARRIVAL]: "",
  [FORM_FIELDS.FLAG_STATE_OF_SHIP]: "",
  [FORM_FIELDS.NAME_OF_MASTER]: "",
  [FORM_FIELDS.LAST_PORT_OF_CALL]: "",
  [FORM_FIELDS.CERTIFICATE_OF_REGISTRY]: "",
  [FORM_FIELDS.SHIP_AGENT_CONTACT]: "",
  [FORM_FIELDS.GROSS_TONNAGE]: "",
  [FORM_FIELDS.NET_TONNAGE]: "",
  [FORM_FIELDS.VOYAGE_PARTICULARS]: "",
  [FORM_FIELDS.CARGO_DESCRIPTION]: "",
  [FORM_FIELDS.NUMBER_OF_CREW]: "",
  [FORM_FIELDS.NUMBER_OF_PASSENGERS]: "",
  [FORM_FIELDS.REMARKS]: "",
  [FORM_FIELDS.WASTERESIDUES_REQUIREMENTS]: "",
  [FORM_FIELDS.COMPLETED_SIGNED_DOCUMENT_URL]: [],
  [FORM_FIELDS.PORT_CALLS]: [],
  [FORM_FIELDS.CARGO_DECLARATION_DOCUMENT_URL]: [],
  [FORM_FIELDS.SHIPS_STORE_DECLARATION_DOCUMENT_URL]: [],
  [FORM_FIELDS.CREWS_EFFECTS_DECLARATION_DOCUMENT_URL]: [],
  [FORM_FIELDS.DANGEROUS_GOODS_MANIFEST_DOCUMENT_URL]: [],
  crew: [],
  passengers: [],
  [FORM_FIELDS.ADDITIONAL_REMARKS]: "",
  [FORM_FIELDS.CALL_ADDITIONAL_DOCUMENTS]: [],
  addPassengerDraft: null,
  addCrewDraft: null,
  addPortCallDraft: null,
};

export const useVesselCallFormStore = create<VesselCallFormState>()(
  persist(
    (set) => ({
      ...(defaultValues as VesselCallFormState),

      setField: (key, val) =>
        set((state) => {
          const newState = cloneDeep(state);
          lodashSet(newState, key, val);
          return newState;
        }),

      setAll: (data: Partial<VesselCallFormState>) =>
        set((s) => ({
          ...s,
          ...data,
          portCalls: data.portCalls
            ? data.portCalls.map((pc) => ({
                id: (pc as PortCall).id ?? "",
                port: pc.port,
                country: pc.country,
                securityLevelOfPortCall: pc.securityLevelOfPortCall,
                arrivalDate: pc.arrivalDate,
                departureDate: pc.departureDate,
                comments: pc.comments,
              }))
            : s.portCalls,
        })),

      resetForm: () => set(() => ({ ...(defaultValues as VesselCallFormState) })),
      addCrew: (member) =>
        set((s) => ({
          ...s,
          crew: [...s.crew, member],
        })),
      setCrew: (crew) =>
        set((s) => ({
          ...s,
          crew,
        })),
      addPortCall: (portCall) =>
        set((s) => ({
          ...s,
          portCalls: sortPortCallsByClosestDates([...s.portCalls, portCall]),
        })),
      setPortCalls: (portCalls) =>
        set((s) => ({
          ...s,
          portCalls,
        })),
      addPassenger: (p) =>
        set((s) => ({
          ...s,
          passengers: [...s.passengers, p],
        })),
      setPassengers: (ps) =>
        set((s) => ({
          ...s,
          passengers: ps,
        })),
      setAddPassengerDraft: (draft) =>
        set((s) => ({
          ...s,
          addPassengerDraft: draft,
        })),
      clearAddPassengerDraft: () =>
        set((s) => ({
          ...s,
          addPassengerDraft: null,
        })),
      setAddCrewDraft: (draft) =>
        set((s) => ({
          ...s,
          addCrewDraft: draft,
        })),
      clearAddCrewDraft: () =>
        set((s) => ({
          ...s,
          addCrewDraft: null,
        })),
      setAddPortCallDraft: (draft) =>
        set((s) => ({
          ...s,
          addPortCallDraft: draft,
        })),
      clearAddPortCallDraft: () =>
        set((s) => ({
          ...s,
          addPortCallDraft: null,
        })),
    }),
    { name: PERSIST_KEY },
  ),
);
